import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

interface Author {
  id: number;
  username: string;
  name: string;
  email: string;
  profilePic: string | null;
  coverPic: string | null;
  createdAt: string;
  _count: {
    posts: number;
    followers: number;
    followings: number;
  };
}

interface Image {
  id: number;
  basename: string;
  createdAt: string;
}

interface Like {
  id: number;
  likedBy: {
    id: number;
    username: string;
    name: string;
    profilePic: string | null;
  };
  createdAt: string;
}

interface Comment {
  id: number;
  comment: string;
  commentBy: {
    id: number;
    username: string;
    name: string;
    profilePic: string | null;
  };
  createdAt: string;
}

interface Hashtag {
  hashtag: {
    id: number;
    tag: string;
  };
}

interface Post {
  id: number;
  description: string;
  createdAt: string;
  author: Author;
  images: Image[];
  likes: Like[];
  comments: Comment[];
  hashtags: Hashtag[];
  _count: {
    likes: number;
    comments: number;
  };
}

const PostDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setIsLoading(true);
      setError('');

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/posts/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPost(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || '게시물을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/admin/posts/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      navigate('/posts');
    } catch (err: any) {
      setError(err.response?.data?.message || '게시물 삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!post) {
    return <Alert severity="info">게시물을 찾을 수 없습니다.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/posts')}>
            목록으로
          </Button>
          <Typography variant="h4">게시물 상세</Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          삭제
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* 게시물 내용 */}
        <Box sx={{ flex: { xs: 1, md: 2 } }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              게시물 내용
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {post.description}
            </Typography>

            {post.hashtags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {post.hashtags.map((hashtag, index) => (
                  <Chip
                    key={index}
                    label={`#${hashtag.hashtag.tag}`}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
              <Chip
                icon={<FavoriteIcon />}
                label={`좋아요 ${post._count.likes}`}
                color="error"
                variant="outlined"
              />
              <Chip
                icon={<CommentIcon />}
                label={`댓글 ${post._count.comments}`}
                color="primary"
                variant="outlined"
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              작성일: {formatDate(post.createdAt)}
            </Typography>
          </Paper>

          {/* 이미지 */}
          {post.images.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                이미지 ({post.images.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {post.images.map((image) => (
                  <Box
                    key={image.id}
                    component="img"
                    src={`${API_BASE_URL}/uploads/posts/${image.basename}`}
                    alt="Post image"
                    sx={{
                      width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' },
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* 댓글 */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              댓글 ({post.comments.length > 10 ? `최근 10개 / 총 ${post._count.comments}개` : post.comments.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {post.comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                댓글이 없습니다.
              </Typography>
            ) : (
              <List>
                {post.comments.map((comment) => (
                  <ListItem key={comment.id} alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar src={comment.commentBy.profilePic || undefined}>
                        {comment.commentBy.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">
                            {comment.commentBy.name}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              @{comment.commentBy.username}
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(comment.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={comment.comment}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Box>

        {/* 작성자 정보 */}
        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              작성자 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={post.author.profilePic || undefined}
                sx={{ width: 60, height: 60, mr: 2 }}
              >
                {post.author.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1">{post.author.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  @{post.author.username}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>이메일:</strong> {post.author.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>게시물:</strong> {post.author._count.posts}개
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>팔로워:</strong> {post.author._count.followers}명
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>팔로잉:</strong> {post.author._count.followings}명
            </Typography>
            <Typography variant="caption" color="text.secondary">
              가입일: {formatDate(post.author.createdAt)}
            </Typography>
          </Paper>

          {/* 좋아요 목록 */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              좋아요 ({post.likes.length > 10 ? `최근 10개 / 총 ${post._count.likes}개` : post.likes.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {post.likes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                좋아요가 없습니다.
              </Typography>
            ) : (
              <List dense>
                {post.likes.map((like) => (
                  <ListItem key={like.id}>
                    <ListItemAvatar>
                      <Avatar
                        src={like.likedBy.profilePic || undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {like.likedBy.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={like.likedBy.name}
                      secondary={`@${like.likedBy.username}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>게시물 삭제 확인</DialogTitle>
        <DialogContent>
          이 게시물을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostDetail;
